import { observer } from "mobx-react";
import { FormikErrors } from "formik";

// hooks
import { useStage04ViewModel } from "./Stage04.vm";

// components
import { Divider, Grid, Typography } from "@mui/material";
import { Label } from "../../../../../../../../shared/ui/label/Label";
import { Input } from "../../../../../../../../shared/ui/input/Input";
import { SingleSelect } from "../../../../../../../../shared/ui/singleSelect/SingleSelect";
import { RadioBox } from "../../../../../../../../shared/ui/radioBox/RadioBox";
import { DateInput } from "../../../../../../../../shared/ui/date/Date";

// lib
import theme from "../../../../../../../../../theme/theme";

//icons
import { ReactComponent as Bin } from "../../../../../../../../../assets/icon/abbvieBin.svg";
import { ReactComponent as Plus } from "../../../../../../../../../assets/icon/abbviePlus.svg";

//types, helpers, styles
import { IObservations } from "./type";
import { ICombinations } from "../stage03/type";
import { combinationsOptions, vasOptions } from "./Stage04.helper";
import { RadioForm } from "../../../styles";
import { ButtonAddDrug } from "../stage02/Stage02.styled";
import React from "react";

export const Stage04 = observer(() => {
  const {
    formik,
    setOption,
    modifyObservations,
    addCombination,
    deleteCombination,
    setCombinationOption,
    addOtherCombination,
    handleYes,
    handleNo,
    addObservation,
    deleteMode,
    deleteObservation,
  } = useStage04ViewModel();

  return (
    <Grid
      container
      maxWidth={"714px"}
      display={"flex"}
      margin={"0 auto"}
      my={4}
      component="form"
    >
      <Grid item mb={5} display={"flex"} flexDirection={"column"}>
        <Label
          size={"small"}
          isUpperCase={true}
          color={theme.colors.grey1}
          label={"Czy lek Rinvoq był używany w pierwszej linii leczenia?"}
          isRequired={true}
        />
        <RadioForm>
          <RadioBox
            isError={formik.touched.wasUsed && Boolean(formik.errors.wasUsed)}
            id={"wasUsed"}
            name={"wasUsed"}
            value={"tak"}
            checked={formik.values.wasUsed === "tak"}
            onChange={(e) => {
              handleYes();
              formik.handleChange(e);
            }}
          />
          <RadioBox
            isError={formik.touched.wasUsed && Boolean(formik.errors.wasUsed)}
            id={"wasUsed2"}
            name={"wasUsed"}
            value={"nie"}
            checked={formik.values.wasUsed === "nie"}
            onChange={(e) => {
              handleNo();
              formik.handleChange(e);
            }}
          />
        </RadioForm>
      </Grid>
      <Grid item xs={12} mb={2}>
        <Typography
          variant={"h6"}
          color={theme.colors.grey1}
          textTransform={"uppercase"}
          fontWeight={700}
        >
          Wyniki badań przed włączeniem leku Rinvoq
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <DateInput
          disableFuture={true}
          openTo={"year"}
          inputFormat={"yyyy/MM/dd"}
          views={["year", "month", "day"]}
          isRequired={true}
          maxWidth={"254px"}
          label={<>Data włączenia leku</>}
          isUpperCase={true}
          name={"drugInitiationDate"}
          size={"small"}
          color={theme.colors.grey1}
          value={formik.values.drugInitiationDate}
          onClose={() => {
            formik.setFieldTouched("drugInitiationDate", true);
            formik.validateField("drugInitiationDate");
          }}
          onChange={(e, context) => {
            formik.setFieldValue("drugInitiationDate", e);
          }}
          onBlur={() => {
            formik.setFieldTouched("drugInitiationDate", true);
            setTimeout(() => {
              formik.validateField("drugInitiationDate");
            }, 0);
          }}
          isError={
            formik.touched.drugInitiationDate &&
            Boolean(formik.errors.drugInitiationDate)
          }
        />
      </Grid>
      <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
        <Grid item pb={2} display={"flex"} flexDirection={"column"}>
          <Label
            size={"small"}
            isUpperCase={true}
            color={theme.colors.grey1}
            label={"Wynik das 28"}
            isRequired={true}
          />
          <RadioForm>
            <RadioBox
              isError={
                formik.touched.das28Result3 &&
                Boolean(formik.errors.das28Result3)
              }
              id={"das28Result3"}
              name={"das28Result3"}
              value={"crp"}
              checked={formik.values.das28Result3 === "crp"}
              onChange={formik.handleChange}
            />
            <RadioBox
              isError={
                formik.touched.das28Result3 &&
                Boolean(formik.errors.das28Result3)
              }
              id={"das28Result32"}
              name={"das28Result3"}
              value={"ob"}
              checked={formik.values.das28Result3 === "ob"}
              onChange={formik.handleChange}
            />
          </RadioForm>
        </Grid>
        <Grid>
          <Input
            isRequired={true}
            label={"Wynik"}
            isUpperCase={true}
            name={"result3"}
            type={"number"}
            maxWidth={"163px"}
            onChangeCustom={() => formik.setFieldValue("result3", null)}
            colorLabel={theme.colors.grey1}
            value={formik.values.result3}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            borderColor={theme.colors.grey5}
            placeholderColor={theme.colors.grey4}
            background={theme.colors.white}
            placeholder={"Wpisz"}
            isError={formik.touched.result3 && Boolean(formik.errors.result3)}
          />
        </Grid>
        <Grid
          item
          justifyContent={"space-between"}
          pb={2}
          display={"flex"}
          flexDirection={"column"}
        >
          <Label
            size={"small"}
            isUpperCase={true}
            color={theme.colors.grey1}
            label={"Wynik skali"}
          />
          <Typography variant="caption" color={theme.colors.grey1}>
            &lt;2,6 - remisja <br />
            &le;3,2 – aktywność mała <br />
            &gt;3,2 i &le;5,1 – aktywność umiarkowana <br />
            &gt;5,1 – aktywność duża
          </Typography>
        </Grid>
      </Grid>
      <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
        <Grid item xs={2}>
          <SingleSelect
            isRequired={true}
            defaultValue={"Wybierz"}
            isError={
              formik.touched.vasResult3 && Boolean(formik.errors.vasResult3)
            }
            label={"Wynik Vas"}
            handleChange={(value) => setOption(value, "vasResult3")}
            selectedValue={formik.values.vasResult3 ?? ""}
            elementName={"vasResult3"}
            options={vasOptions}
          />
        </Grid>
        {formik.values.das28Result3 === "crp" && (
          <Grid item xs={2}>
            <Input
              isRequired={true}
              label={"Wynik OB"}
              isUpperCase={true}
              name={"obResult3"}
              type={"number"}
              maxWidth={"163px"}
              onChangeCustom={() => formik.setFieldValue("obResult3", null)}
              colorLabel={theme.colors.grey1}
              value={formik.values.obResult3}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              borderColor={theme.colors.grey5}
              placeholderColor={theme.colors.grey4}
              background={theme.colors.white}
              placeholder={"Wpisz"}
              isError={
                formik.touched.obResult3 && Boolean(formik.errors.obResult3)
              }
            />
          </Grid>
        )}
        {formik.values.das28Result3 === "ob" && (
          <Grid>
            <Input
              isRequired={true}
              label={"Wynik CRP"}
              isUpperCase={true}
              name={"crpResult3"}
              type={"number"}
              maxWidth={"163px"}
              onChangeCustom={() => formik.setFieldValue("crpResult3", null)}
              colorLabel={theme.colors.grey1}
              value={formik.values.crpResult3}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              borderColor={theme.colors.grey5}
              placeholderColor={theme.colors.grey4}
              background={theme.colors.white}
              placeholder={"Wpisz"}
              isError={
                formik.touched.crpResult3 && Boolean(formik.errors.crpResult3)
              }
            />
          </Grid>
        )}
      </Grid>
      <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
        <Grid>
          <Input
            isRequired={true}
            label={"Liczba bolesnych stawów"}
            isUpperCase={true}
            name={"tenderJointsCount3"}
            type={"number"}
            maxWidth={"72px"}
            onChangeCustom={() =>
              formik.setFieldValue("tenderJointsCount3", null)
            }
            colorLabel={theme.colors.grey1}
            value={formik.values.tenderJointsCount3}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            borderColor={theme.colors.grey5}
            placeholderColor={theme.colors.grey4}
            background={theme.colors.white}
            placeholder={"Wpisz"}
            isError={
              formik.touched.tenderJointsCount3 &&
              Boolean(formik.errors.tenderJointsCount3)
            }
          />
        </Grid>
        <Grid>
          <Input
            isRequired={true}
            label={"Liczba obrzękniętych stawów"}
            isUpperCase={true}
            name={"swollenJointsCount3"}
            type={"number"}
            maxWidth={"72px"}
            onChangeCustom={() =>
              formik.setFieldValue("swollenJointsCount3", null)
            }
            colorLabel={theme.colors.grey1}
            value={formik.values.swollenJointsCount3}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            borderColor={theme.colors.grey5}
            placeholderColor={theme.colors.grey4}
            background={theme.colors.white}
            placeholder={"Wpisz"}
            isError={
              formik.touched.swollenJointsCount3 &&
              Boolean(formik.errors.swollenJointsCount3)
            }
          />
        </Grid>
      </Grid>
      <Grid item xs={12} mb={2} mt={3}>
        <Typography
          variant={"h6"}
          color={theme.colors.grey1}
          textTransform={"uppercase"}
          fontWeight={700}
        >
          Obserwacja na leku Rinvoq
        </Typography>
      </Grid>
      <Grid container display={"flex"} gap={"20px"} mb={2}>
        <Grid item pb={2} display={"flex"} flexDirection={"column"}>
          <Label
            size={"small"}
            isUpperCase={true}
            color={theme.colors.grey1}
            label={"Interwały obserwacji"}
            isRequired={true}
          />
          <RadioForm>
            <RadioBox
              isError={
                formik.touched.observationIntervals &&
                Boolean(formik.errors.observationIntervals)
              }
              id={"observationIntervals"}
              name={"observationIntervals"}
              value={"co 1 msc."}
              checked={formik.values.observationIntervals === "co 1 msc."}
              onChange={(e) => {
                formik.handleChange(e);
                modifyObservations(e.target.value);
              }}
            />
            <RadioBox
              isError={
                formik.touched.observationIntervals &&
                Boolean(formik.errors.observationIntervals)
              }
              id={"observationIntervals2"}
              name={"observationIntervals"}
              value={"co 3 msc."}
              checked={formik.values.observationIntervals === "co 3 msc."}
              onChange={(e) => {
                formik.handleChange(e);
                modifyObservations(e.target.value);
              }}
            />
            <RadioBox
              isError={
                formik.touched.observationIntervals &&
                Boolean(formik.errors.observationIntervals)
              }
              id={"observationIntervals3"}
              name={"observationIntervals"}
              value={"co 6 msc."}
              checked={formik.values.observationIntervals === "co 6 msc."}
              onChange={(e) => {
                formik.handleChange(e);
                modifyObservations(e.target.value);
              }}
            />
          </RadioForm>
        </Grid>
      </Grid>
      {formik.values.observations.length > 0 && (
        <>
          <Divider
            style={{ width: "100%" }}
            variant={"fullWidth"}
            sx={{ height: "2px", margin: "0 0 24px 0" }}
          />
          <Grid
            item
            xs={12}
            mb={2}
            mt={2}
            display={"flex"}
            justifyContent={"center"}
          >
            <Typography
              variant={"h6"}
              color={theme.colors.grey1}
              textTransform={"uppercase"}
              fontWeight={700}
            >
              Obserwacje
            </Typography>
          </Grid>
        </>
      )}
      <Grid container>
        {formik.values.observations?.map((line, index) => (
          <Grid
            key={index}
            item
            xs={12}
            mb={4}
            style={{
              boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              padding: "0 16px",
              backgroundColor: "rgba(255, 255, 255, 0.70)",
            }}
          >
            <Grid
              item
              xs={12}
              mt={2}
              mb={4}
              justifyContent={"space-between"}
              display={"flex"}
            >
              <Typography
                variant={"h6"}
                color={theme.colors.grey1}
                textTransform={"uppercase"}
                fontWeight={700}
              >
                Obserwacja {index + 1}
              </Typography>
              {deleteMode && (
                <ButtonAddDrug
                  type="button"
                  onClick={() => deleteObservation(index)}
                >
                  Usuń obserwację
                  <Bin />
                </ButtonAddDrug>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              justifyContent={"space-between"}
              pb={2}
              display={"flex"}
              flexDirection={"column"}
            >
              <Label
                size={"small"}
                isUpperCase={true}
                color={theme.colors.grey1}
                label={"Monoterapia czy kombinacja z innym lekiem?"}
                isRequired={true}
              />
              <RadioForm>
                <RadioBox
                  isError={
                    Array.isArray(formik.touched.observations) &&
                    formik.touched.observations[index]?.monotherapy &&
                    Array.isArray(formik.errors.observations) &&
                    Boolean(
                      (
                        formik.errors.observations[
                          index
                        ] as FormikErrors<IObservations>
                      )?.monotherapy
                    )
                  }
                  id={`observations.monotherapy1-${index}`}
                  name={`observations[${index}].monotherapy`}
                  value={"monoterapia"}
                  checked={
                    formik.values.observations[index].monotherapy ===
                    "monoterapia"
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      `observations[${index}].monotherapy`,
                      e.target.value
                    );
                    formik.setFieldValue(
                      `observations[${index}].combinationWithMedicine`,
                      []
                    );
                  }}
                />
                <RadioBox
                  isError={
                    Array.isArray(formik.touched.observations) &&
                    formik.touched.observations[index]?.monotherapy &&
                    Array.isArray(formik.errors.observations) &&
                    Boolean(
                      (
                        formik.errors.observations[
                          index
                        ] as FormikErrors<IObservations>
                      )?.monotherapy
                    )
                  }
                  id={`observations.monotherapy2-${index}`}
                  name={`observations[${index}].monotherapy`}
                  value={"kombinacja"}
                  checked={
                    formik.values.observations[index].monotherapy ===
                    "kombinacja"
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      `observations[${index}].monotherapy`,
                      e.target.value
                    );
                    addCombination(index);
                  }}
                />
              </RadioForm>
            </Grid>
            {formik.values.observations &&
              formik.values.observations.length > 0 &&
              formik.values.observations[index]?.combinationWithMedicine
                .length > 0 &&
              formik.values.observations[index]?.combinationWithMedicine?.map(
                (combination, idx) => (
                  <Grid
                    style={{ backgroundColor: "rgba(7, 28, 71, 0.10)" }}
                    key={`observation.combination-${index}${idx}`}
                  >
                    <Grid key={idx} item xs={12} p={2} mb={2}>
                      <Grid
                        item
                        xs={12}
                        mt={2}
                        justifyContent={"space-between"}
                        display={"flex"}
                      >
                        <Typography
                          variant={"h6"}
                          color={theme.colors.grey1}
                          textTransform={"uppercase"}
                          fontWeight={700}
                        >
                          Kombinacja {idx + 1}
                        </Typography>
                        {formik.values.observations[index]
                          .combinationWithMedicine?.length !== 1 && (
                          <ButtonAddDrug
                            type="button"
                            onClick={() => deleteCombination(index, idx)}
                          >
                            Usuń kombinację
                            <Bin />
                          </ButtonAddDrug>
                        )}
                      </Grid>
                      <Grid item xs={12} mt={1}>
                        <SingleSelect
                          isRequired={true}
                          defaultValue={"Wybierz"}
                          isError={
                            Array.isArray(formik.touched.observations) &&
                            Array.isArray(formik.errors.observations) &&
                            typeof formik.errors.observations[index] ===
                              "object" &&
                            formik.touched.observations[index]
                              ?.combinationWithMedicine?.[idx]
                              ?.combinationDrugName &&
                            Boolean(
                              (
                                (
                                  formik.errors.observations[
                                    index
                                  ] as FormikErrors<IObservations>
                                )?.combinationWithMedicine?.[
                                  idx
                                ] as FormikErrors<ICombinations>
                              )?.combinationDrugName
                            )
                          }
                          label={"Kombinacja z jakim lekiem"}
                          handleChange={(value) =>
                            setCombinationOption(
                              value,
                              "combinationDrugName",
                              index,
                              idx
                            )
                          }
                          selectedValue={
                            formik.values.observations[index]
                              ?.combinationWithMedicine[idx]
                              .combinationDrugName || ""
                          }
                          elementName={`observations[${index}].combinationWithMedicine[${idx}].combinationDrugName`}
                          options={combinationsOptions}
                        />
                      </Grid>
                      {formik.values.observations[index]
                        .combinationWithMedicine[idx].combinationDrugName ===
                        "Inne" && (
                        <Grid item xs={12}>
                          <Input
                            isRequired={true}
                            label={"nazwa innego leku*"}
                            isUpperCase={true}
                            name={`observations[${index}].combinationWithMedicine[${idx}].otherCombinationDrug`}
                            type={"text"}
                            colorLabel={theme.colors.grey1}
                            value={
                              formik.values.observations?.[index]
                                ?.combinationWithMedicine[idx]
                                .otherCombinationDrug || ""
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            borderColor={theme.colors.grey5}
                            placeholderColor={theme.colors.grey4}
                            background={theme.colors.white}
                            placeholder={"Wpisz"}
                            isError={
                              Array.isArray(formik.touched.observations) &&
                              Array.isArray(formik.errors.observations) &&
                              typeof formik.errors.observations[index] ===
                                "object" &&
                              formik.touched.observations[index]
                                ?.combinationWithMedicine?.[idx]
                                ?.otherCombinationDrug &&
                              Boolean(
                                (
                                  (
                                    formik.errors.observations[
                                      index
                                    ] as FormikErrors<IObservations>
                                  )?.combinationWithMedicine?.[
                                    idx
                                  ] as FormikErrors<ICombinations>
                                )?.otherCombinationDrug
                              )
                            }
                          />
                        </Grid>
                      )}
                      <Grid
                        container
                        display={"flex"}
                        gap={"10px"}
                        alignItems={"flex-end"}
                      >
                        <Grid>
                          <Input
                            isRequired={true}
                            label={"Dawka"}
                            isUpperCase={true}
                            name={`observations[${index}].combinationWithMedicine[${idx}].combinationDose`}
                            type={"number"}
                            onChangeCustom={() =>
                              formik.setFieldValue(
                                `observations[${index}].combinationWithMedicine[${idx}].combinationDose`,
                                null
                              )
                            }
                            maxWidth={"163px"}
                            colorLabel={theme.colors.grey1}
                            value={
                              formik.values.observations?.[index]
                                ?.combinationWithMedicine[idx].combinationDose
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            borderColor={theme.colors.grey5}
                            placeholderColor={theme.colors.grey4}
                            background={theme.colors.white}
                            placeholder={"Wpisz"}
                            isError={
                              Array.isArray(formik.touched.observations) &&
                              Array.isArray(formik.errors.observations) &&
                              typeof formik.errors.observations[index] ===
                                "object" &&
                              formik.touched.observations[index]
                                ?.combinationWithMedicine?.[idx]
                                ?.combinationDose &&
                              Boolean(
                                (
                                  (
                                    formik.errors.observations[
                                      index
                                    ] as FormikErrors<IObservations>
                                  )?.combinationWithMedicine?.[
                                    idx
                                  ] as FormikErrors<ICombinations>
                                )?.combinationDose
                              )
                            }
                          />
                        </Grid>
                        <Grid pb={2}>
                          <RadioForm>
                            <RadioBox
                              id={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit1`}
                              name={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit`}
                              value={"g/m"}
                              isUpperCase={false}
                              checked={combination.combinationUnit === "g/m"}
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `observations[${index}].combinationWithMedicine[${idx}].combinationUnit`,
                                  e.target.value
                                )
                              }
                              isError={
                                Array.isArray(formik.touched.observations) &&
                                Array.isArray(formik.errors.observations) &&
                                typeof formik.errors.observations[index] ===
                                  "object" &&
                                formik.touched.observations[index]
                                  ?.combinationWithMedicine?.[idx]
                                  ?.combinationUnit &&
                                Boolean(
                                  (
                                    (
                                      formik.errors.observations[
                                        index
                                      ] as FormikErrors<IObservations>
                                    )?.combinationWithMedicine?.[
                                      idx
                                    ] as FormikErrors<ICombinations>
                                  )?.combinationUnit
                                )
                              }
                            />
                            <RadioBox
                              id={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit2`}
                              name={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit`}
                              value={"mg/kg m.c."}
                              isUpperCase={false}
                              checked={
                                combination.combinationUnit === "mg/kg m.c."
                              }
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `observations[${index}].combinationWithMedicine[${idx}].combinationUnit`,
                                  e.target.value
                                )
                              }
                              isError={
                                Array.isArray(formik.touched.observations) &&
                                Array.isArray(formik.errors.observations) &&
                                typeof formik.errors.observations[index] ===
                                  "object" &&
                                formik.touched.observations[index]
                                  ?.combinationWithMedicine?.[idx]
                                  ?.combinationUnit &&
                                Boolean(
                                  (
                                    (
                                      formik.errors.observations[
                                        index
                                      ] as FormikErrors<IObservations>
                                    )?.combinationWithMedicine?.[
                                      idx
                                    ] as FormikErrors<ICombinations>
                                  )?.combinationUnit
                                )
                              }
                            />
                            <RadioBox
                              id={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit3`}
                              name={`observations[${index}].combinationWithMedicine[${idx}].combinationUnit`}
                              value={"Inna jednostka"}
                              isUpperCase={false}
                              checked={
                                combination.combinationUnit === "Inna jednostka"
                              }
                              onChange={(e) =>
                                formik.setFieldValue(
                                  `observations[${index}].combinationWithMedicine[${idx}].combinationUnit`,
                                  e.target.value
                                )
                              }
                              isError={
                                Array.isArray(formik.touched.observations) &&
                                Array.isArray(formik.errors.observations) &&
                                typeof formik.errors.observations[index] ===
                                  "object" &&
                                formik.touched.observations[index]
                                  ?.combinationWithMedicine?.[idx]
                                  ?.combinationUnit &&
                                Boolean(
                                  (
                                    (
                                      formik.errors.observations[
                                        index
                                      ] as FormikErrors<IObservations>
                                    )?.combinationWithMedicine?.[
                                      idx
                                    ] as FormikErrors<ICombinations>
                                  )?.combinationUnit
                                )
                              }
                            />
                          </RadioForm>
                        </Grid>
                        {formik.values.observations[index]
                          .combinationWithMedicine[idx].combinationUnit ===
                          "Inna jednostka" && (
                          <Grid>
                            <Input
                              isRequired={true}
                              label={"Inna jednostka"}
                              isUpperCase={true}
                              name={`observations[${index}].combinationWithMedicine[${idx}].otherCombinationUnit`}
                              type={"text"}
                              maxWidth={"163px"}
                              colorLabel={theme.colors.grey1}
                              value={
                                formik.values.observations?.[index]
                                  ?.combinationWithMedicine[idx]
                                  .otherCombinationUnit || ""
                              }
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              borderColor={theme.colors.grey5}
                              placeholderColor={theme.colors.grey4}
                              background={theme.colors.white}
                              placeholder={"Wpisz"}
                              isError={
                                Array.isArray(formik.touched.observations) &&
                                Array.isArray(formik.errors.observations) &&
                                typeof formik.errors.observations[index] ===
                                  "object" &&
                                formik.touched.observations[index]
                                  ?.combinationWithMedicine?.[idx]
                                  ?.otherCombinationUnit &&
                                Boolean(
                                  (
                                    (
                                      formik.errors.observations[
                                        index
                                      ] as FormikErrors<IObservations>
                                    )?.combinationWithMedicine?.[
                                      idx
                                    ] as FormikErrors<ICombinations>
                                  )?.otherCombinationUnit
                                )
                              }
                            />
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <Input
                            before={
                              <Label
                                isUpperCase={false}
                                style={{ margin: "8px 0 0" }}
                                color={theme.colors.orange}
                                size={"superSmall"}
                                label={"liczba tygodni"}
                              />
                            }
                            maxWidth={"72px"}
                            isRequired={true}
                            label={"Zakres czasu przyjmowania"}
                            isUpperCase={true}
                            name={`observations[${index}].combinationWithMedicine[${idx}].combinationDuration`}
                            type={"number"}
                            onChangeCustom={() =>
                              formik.setFieldValue(
                                `observations[${index}].combinationWithMedicine[${idx}].combinationDuration`,
                                null
                              )
                            }
                            colorLabel={theme.colors.grey1}
                            value={
                              formik.values.observations?.[index]
                                ?.combinationWithMedicine[idx]
                                .combinationDuration
                            }
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            borderColor={theme.colors.grey5}
                            placeholderColor={theme.colors.grey4}
                            background={theme.colors.white}
                            placeholder={"Wpisz"}
                            isError={
                              Array.isArray(formik.touched.observations) &&
                              Array.isArray(formik.errors.observations) &&
                              typeof formik.errors.observations[index] ===
                                "object" &&
                              formik.touched.observations[index]
                                ?.combinationWithMedicine?.[idx]
                                ?.combinationDuration &&
                              Boolean(
                                (
                                  (
                                    formik.errors.observations[
                                      index
                                    ] as FormikErrors<IObservations>
                                  )?.combinationWithMedicine?.[
                                    idx
                                  ] as FormikErrors<ICombinations>
                                )?.combinationDuration
                              )
                            }
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                )
              )}

            {formik.values.observations[index].monotherapy === "kombinacja" && (
              <Grid
                mt={4}
                mb={2}
                display={"flex"}
                item
                xs={12}
                justifyContent={"center"}
              >
                <ButtonAddDrug
                  type="button"
                  onClick={() => addOtherCombination(index)}
                >
                  Dodaj kombinację z lekiem
                  <Plus />
                </ButtonAddDrug>
              </Grid>
            )}

            <Divider
              style={{ width: "100%" }}
              variant={"fullWidth"}
              sx={{ height: "2px", margin: "0 0 16px 0" }}
            />

            <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
              <Grid item pb={2} display={"flex"} flexDirection={"column"}>
                <Label
                  size={"small"}
                  isUpperCase={true}
                  color={theme.colors.grey1}
                  label={"Wynik das 28"}
                  isRequired={true}
                />
                <RadioForm>
                  <RadioBox
                    isError={
                      Array.isArray(formik.touched.observations) &&
                      formik.touched.observations[index]?.das28Result &&
                      Array.isArray(formik.errors.observations) &&
                      Boolean(
                        (
                          formik.errors.observations[
                            index
                          ] as FormikErrors<IObservations>
                        )?.das28Result
                      )
                    }
                    id={`observations.das28Result1-${index}`}
                    name={`observations[${index}].das28Result`}
                    value={"crp"}
                    checked={
                      formik.values.observations[index].das28Result === "crp"
                    }
                    onChange={(e) => {
                      formik.setFieldValue(
                        `observations[${index}].das28Result`,
                        e.target.value
                      );
                    }}
                  />
                  <RadioBox
                    isError={
                      Array.isArray(formik.touched.observations) &&
                      formik.touched.observations[index]?.das28Result &&
                      Array.isArray(formik.errors.observations) &&
                      Boolean(
                        (
                          formik.errors.observations[
                            index
                          ] as FormikErrors<IObservations>
                        )?.das28Result
                      )
                    }
                    id={`observations.das28Result2-${index}`}
                    name={`observations[${index}].das28Result`}
                    value={"ob"}
                    checked={
                      formik.values.observations[index].das28Result === "ob"
                    }
                    onChange={(e) => {
                      formik.setFieldValue(
                        `observations[${index}].das28Result`,
                        e.target.value
                      );
                    }}
                  />
                </RadioForm>
              </Grid>
              <Grid>
                <Input
                  isRequired={true}
                  label={"Wynik"}
                  isUpperCase={true}
                  name={`observations[${index}].result`}
                  type={"number"}
                  onChangeCustom={() =>
                    formik.setFieldValue(`observations[${index}].result`, null)
                  }
                  maxWidth={"163px"}
                  colorLabel={theme.colors.grey1}
                  value={formik.values.observations?.[index]?.result}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={theme.colors.grey5}
                  placeholderColor={theme.colors.grey4}
                  background={theme.colors.white}
                  placeholder={"Wpisz"}
                  isError={
                    formik.touched.observations?.[index]?.result &&
                    Boolean(
                      (
                        formik.errors.observations?.[
                          index
                        ] as FormikErrors<IObservations>
                      )?.result
                    )
                  }
                />
              </Grid>
              <Grid
                item
                justifyContent={"space-between"}
                pb={2}
                display={"flex"}
                flexDirection={"column"}
              >
                <Label
                  size={"small"}
                  isUpperCase={true}
                  color={theme.colors.grey1}
                  label={"Wynik skali"}
                />
                <Typography variant="caption" color={theme.colors.grey1}>
                  &lt;2,6 - remisja <br />
                  &le;3,2 – aktywność mała <br />
                  &gt;3,2 i &le;5,1 – aktywność umiarkowana <br />
                  &gt;5,1 – aktywność duża
                </Typography>
              </Grid>
            </Grid>

            <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
              <Grid item xs={2}>
                <SingleSelect
                  isRequired={true}
                  defaultValue={"Wybierz"}
                  isError={
                    Array.isArray(formik.touched.observations) &&
                    formik.touched.observations[index]?.vasResult &&
                    Array.isArray(formik.errors.observations) &&
                    Boolean(
                      (
                        formik.errors.observations[
                          index
                        ] as FormikErrors<IObservations>
                      )?.vasResult
                    )
                  }
                  label={"Wynik Vas"}
                  handleChange={(value) => setOption(value, "vasResult", index)}
                  selectedValue={
                    formik.values.observations[index]?.vasResult ?? ""
                  }
                  elementName={`observations[${index}].vasResult`}
                  options={vasOptions}
                />
              </Grid>
              {formik.values.observations[index].das28Result === "crp" && (
                <Grid item xs={2}>
                  <Input
                    isRequired={true}
                    label={"Wynik OB"}
                    isUpperCase={true}
                    name={`observations[${index}].obResult`}
                    type={"number"}
                    maxWidth={"163px"}
                    onChangeCustom={() =>
                      formik.setFieldValue(
                        `observations[${index}].obResult`,
                        null
                      )
                    }
                    colorLabel={theme.colors.grey1}
                    value={formik.values.observations?.[index]?.obResult}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    borderColor={theme.colors.grey5}
                    placeholderColor={theme.colors.grey4}
                    background={theme.colors.white}
                    placeholder={"Wpisz"}
                    isError={
                      formik.touched.observations?.[index]?.obResult &&
                      Boolean(
                        (
                          formik.errors.observations?.[
                            index
                          ] as FormikErrors<IObservations>
                        )?.obResult
                      )
                    }
                  />
                </Grid>
              )}
              {formik.values.observations[index].das28Result === "ob" && (
                <Grid>
                  <Input
                    isRequired={true}
                    label={"Wynik CRP"}
                    isUpperCase={true}
                    name={`observations[${index}].crpResult`}
                    type={"number"}
                    maxWidth={"163px"}
                    onChangeCustom={() =>
                      formik.setFieldValue(
                        `observations[${index}].crpResult`,
                        null
                      )
                    }
                    colorLabel={theme.colors.grey1}
                    value={formik.values.observations?.[index]?.crpResult}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    borderColor={theme.colors.grey5}
                    placeholderColor={theme.colors.grey4}
                    background={theme.colors.white}
                    placeholder={"Wpisz"}
                    isError={
                      formik.touched.observations?.[index]?.crpResult &&
                      Boolean(
                        (
                          formik.errors.observations?.[
                            index
                          ] as FormikErrors<IObservations>
                        )?.crpResult
                      )
                    }
                  />
                </Grid>
              )}
            </Grid>
            <Grid container display={"flex"} gap={"20px"} mb={2} mt={2}>
              <Grid>
                <Input
                  isRequired={true}
                  label={"Liczba bolesnych stawów"}
                  isUpperCase={true}
                  type={"number"}
                  name={`observations[${index}].tenderJointsCount`}
                  onChangeCustom={() =>
                    formik.setFieldValue(
                      `observations[${index}].tenderJointsCount`,
                      null
                    )
                  }
                  maxWidth={"72px"}
                  colorLabel={theme.colors.grey1}
                  value={formik.values.observations?.[index]?.tenderJointsCount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={theme.colors.grey5}
                  placeholderColor={theme.colors.grey4}
                  background={theme.colors.white}
                  placeholder={"Wpisz"}
                  isError={
                    formik.touched.observations?.[index]?.tenderJointsCount &&
                    Boolean(
                      (
                        formik.errors.observations?.[
                          index
                        ] as FormikErrors<IObservations>
                      )?.tenderJointsCount
                    )
                  }
                />
              </Grid>
              <Grid>
                <Input
                  isRequired={true}
                  label={"Liczba obrzękniętych stawów"}
                  isUpperCase={true}
                  type={"number"}
                  onChangeCustom={() =>
                    formik.setFieldValue(
                      `observations[${index}].swollenJointsCount`,
                      null
                    )
                  }
                  name={`observations[${index}].swollenJointsCount`}
                  maxWidth={"72px"}
                  colorLabel={theme.colors.grey1}
                  value={
                    formik.values.observations?.[index]?.swollenJointsCount
                  }
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  borderColor={theme.colors.grey5}
                  placeholderColor={theme.colors.grey4}
                  background={theme.colors.white}
                  placeholder={"Wpisz"}
                  isError={
                    formik.touched.observations?.[index]?.swollenJointsCount &&
                    Boolean(
                      (
                        formik.errors.observations?.[
                          index
                        ] as FormikErrors<IObservations>
                      )?.swollenJointsCount
                    )
                  }
                />
              </Grid>
            </Grid>
            <Grid item xs={12} mt={4}>
              <Input
                label={"Zmiany w zakresie skal łuszczycy"}
                isUpperCase={true}
                name={`observations[${index}].psoriasisChanges`}
                type={"textarea"}
                colorLabel={theme.colors.grey1}
                value={formik.values.observations?.[index]?.psoriasisChanges}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                borderColor={theme.colors.grey5}
                placeholderColor={theme.colors.grey4}
                background={theme.colors.white}
                placeholder={"Wpisz"}
                isError={
                  formik.touched.observations?.[index]?.psoriasisChanges &&
                  Boolean(
                    (
                      formik.errors.observations?.[
                        index
                      ] as FormikErrors<IObservations>
                    )?.psoriasisChanges
                  )
                }
              />
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Grid mb={1} display={"flex"} item xs={12} justifyContent={"center"}>
        <ButtonAddDrug type="button" onClick={addObservation}>
          Dodaj obserwację
          <Plus />
        </ButtonAddDrug>
      </Grid>
      <Grid container display={"flex"} gap={"20px"} mb={2}>
        <Grid item pb={2} display={"flex"} flexDirection={"column"}>
          <Label
            size={"small"}
            isUpperCase={true}
            color={theme.colors.grey1}
            label={"Glikokortykosteroidy (GKS)"}
            isRequired={true}
          />
          <RadioForm>
            <RadioBox
              isError={formik.touched.gks && Boolean(formik.errors.gks)}
              id={"gks"}
              name={"gks"}
              value={"tak"}
              checked={formik.values.gks === "tak"}
              onChange={(e) => {
                formik.handleChange(e);
              }}
            />
            <RadioBox
              isError={formik.touched.gks && Boolean(formik.errors.gks)}
              id={"gks2"}
              name={"gks"}
              value={"nie"}
              checked={formik.values.gks === "nie"}
              onChange={(e) => {
                formik.handleChange(e);
              }}
            />
          </RadioForm>
        </Grid>
      </Grid>
    </Grid>
  );
});
